import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Ticket,
  AlertCircle,
  Loader2,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  Paperclip,
  Eye,
  User,
  Plus,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface SupportTicket {
  id: string
  ticket_number: string
  church_id: string
  submitted_by: string | null
  submitter_name: string
  submitter_email: string | null
  submitter_phone: string | null
  category: string
  priority: string
  status: string
  subject: string
  description: string
  assigned_to: string | null
  resolved_at: string | null
  closed_at: string | null
  first_response_at: string | null
  sla_breach: boolean
  tags: string[]
  created_at: string
  updated_at: string
  church_name?: string
  assigned_to_name?: string
  message_count?: number
}

interface TicketMessage {
  id: string
  ticket_id: string
  author_id: string | null
  author_type: string
  author_name: string
  message: string
  is_internal: boolean
  created_at: string
}

interface SupportTicketsProps {
  user: AdminUser | null
}

export default function SupportTickets({ user }: SupportTicketsProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null)
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([])
  const [showTicketDialog, setShowTicketDialog] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [sending, setSending] = useState(false)

  // Create ticket state
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [churches, setChurches] = useState<any[]>([])
  const [newTicket, setNewTicket] = useState({
    church_id: '',
    church_name: '',
    submitter_name: '',
    submitter_email: '',
    submitter_phone: '',
    category: 'technical',
    priority: 'medium',
    subject: '',
    description: ''
  })

  // Filters
  const [activeTab, setActiveTab] = useState('open')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  // Ticket assignment
  const [availableAgents, setAvailableAgents] = useState<any[]>([])
  const [assigning, setAssigning] = useState(false)

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    critical: 0,
    slaBreach: 0,
  })

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access support tickets.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    loadTickets()
    loadAvailableAgents()
    loadChurches()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [tickets, activeTab, searchTerm, statusFilter, priorityFilter, categoryFilter])

  async function loadTickets() {
    try {
      setLoading(true)

      const { data, error } = await supabase
        .from('ticket_summary')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      setTickets(data || [])
      calculateStats(data || [])
    } catch (error) {
      console.error('Error loading tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadAvailableAgents() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('admin_level', ['super_admin', 'church_owner', 'church_admin'])

      if (error) throw error

      setAvailableAgents(data || [])
    } catch (error) {
      console.error('Error loading agents:', error)
    }
  }

  async function loadChurches() {
    try {
      const { data, error } = await supabase
        .from('churches')
        .select('id, name, display_name')
        .order('name')

      if (error) throw error

      setChurches(data || [])
    } catch (error) {
      console.error('Error loading churches:', error)
    }
  }

  async function handleCreateTicket(e: React.FormEvent) {
    e.preventDefault()
    setCreating(true)

    try {
      // Call the generate_ticket_number function to get a unique ticket number
      const { data: ticketNumber, error: funcError } = await supabase
        .rpc('generate_ticket_number')

      if (funcError) throw funcError

      // Insert the ticket
      const { data: ticket, error: insertError } = await supabase
        .from('support_tickets')
        .insert({
          ticket_number: ticketNumber,
          church_id: newTicket.church_id,
          submitter_name: newTicket.submitter_name,
          submitter_email: newTicket.submitter_email || null,
          submitter_phone: newTicket.submitter_phone || null,
          category: newTicket.category,
          priority: newTicket.priority,
          subject: newTicket.subject,
          description: newTicket.description,
          status: 'new'
        })
        .select()
        .single()

      if (insertError) throw insertError

      // Log to audit
      await supabase.rpc('log_audit_event', {
        p_admin_user_id: user!.id,
        p_admin_email: user!.email,
        p_admin_name: `${user!.firstName} ${user!.lastName}`,
        p_action_type: 'ticket_created',
        p_action_category: 'support',
        p_target_church_id: newTicket.church_id,
        p_target_church_name: newTicket.church_name,
        p_action_details: {
          ticket_number: ticketNumber,
          subject: newTicket.subject,
          priority: newTicket.priority,
          category: newTicket.category
        },
        p_severity: 'info'
      })

      // Reset form
      setNewTicket({
        church_id: '',
        church_name: '',
        submitter_name: '',
        submitter_email: '',
        submitter_phone: '',
        category: 'technical',
        priority: 'medium',
        subject: '',
        description: ''
      })

      setShowCreateDialog(false)
      loadTickets() // Refresh the list

      alert(`Ticket ${ticketNumber} created successfully!`)
    } catch (error: any) {
      console.error('Error creating ticket:', error)
      alert('Failed to create ticket: ' + error.message)
    } finally {
      setCreating(false)
    }
  }

  function calculateStats(ticketData: SupportTicket[]) {
    const openTickets = ticketData.filter(t =>
      ['new', 'open', 'in_progress', 'waiting_on_customer'].includes(t.status)
    )
    const inProgressTickets = ticketData.filter(t => t.status === 'in_progress')
    const criticalTickets = openTickets.filter(t => t.priority === 'critical')
    const slaBreach = openTickets.filter(t => t.sla_breach)

    setStats({
      total: ticketData.length,
      open: openTickets.length,
      inProgress: inProgressTickets.length,
      critical: criticalTickets.length,
      slaBreach: slaBreach.length,
    })
  }

  function applyFilters() {
    let filtered = [...tickets]

    // Tab filter
    if (activeTab === 'open') {
      filtered = filtered.filter(t =>
        ['new', 'open', 'in_progress', 'waiting_on_customer'].includes(t.status)
      )
    } else if (activeTab === 'my') {
      filtered = filtered.filter(t => t.assigned_to === user?.id)
    } else if (activeTab === 'resolved') {
      filtered = filtered.filter(t => t.status === 'resolved' || t.status === 'closed')
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.church_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.submitter_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter)
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(t => t.priority === priorityFilter)
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter)
    }

    setFilteredTickets(filtered)
  }

  function getPriorityBadge(priority: string) {
    switch (priority) {
      case 'critical':
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Critical</Badge>
      case 'high':
        return <Badge className="bg-orange-500"><AlertTriangle className="h-3 w-3 mr-1" />High</Badge>
      case 'medium':
        return <Badge className="bg-blue-500">Medium</Badge>
      default:
        return <Badge variant="outline">Low</Badge>
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'new':
        return <Badge className="bg-purple-500">New</Badge>
      case 'open':
        return <Badge className="bg-blue-500">Open</Badge>
      case 'in_progress':
        return <Badge className="bg-yellow-500">In Progress</Badge>
      case 'waiting_on_customer':
        return <Badge className="bg-orange-500">Waiting</Badge>
      case 'resolved':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Resolved</Badge>
      case 'closed':
        return <Badge variant="outline">Closed</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  function getCategoryLabel(category: string) {
    const labels: Record<string, string> = {
      technical: 'Technical Issue',
      billing: 'Billing',
      feature_request: 'Feature Request',
      bug_report: 'Bug Report',
      general: 'General Inquiry',
    }
    return labels[category] || category
  }

  async function handleViewTicket(ticket: SupportTicket) {
    setSelectedTicket(ticket)
    setShowTicketDialog(true)

    // Load ticket messages
    try {
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select('*')
        .eq('ticket_id', ticket.id)
        .order('created_at', { ascending: true })

      if (error) throw error

      setTicketMessages(data || [])
    } catch (error) {
      console.error('Error loading ticket messages:', error)
    }
  }

  async function handleSendMessage() {
    if (!selectedTicket || !user || !newMessage.trim()) return

    try {
      setSending(true)

      const { error: messageError } = await supabase
        .from('support_ticket_messages')
        .insert({
          ticket_id: selectedTicket.id,
          author_id: user.id,
          author_type: 'support',
          author_name: `${user.firstName} ${user.lastName}`,
          message: newMessage,
          is_internal: isInternal,
        })

      if (messageError) throw messageError

      // Update first response timestamp if this is the first support message
      if (!selectedTicket.first_response_at) {
        await supabase
          .from('support_tickets')
          .update({ first_response_at: new Date().toISOString() })
          .eq('id', selectedTicket.id)
      }

      // Update ticket status to open if it's new
      if (selectedTicket.status === 'new') {
        await supabase
          .from('support_tickets')
          .update({ status: 'open' })
          .eq('id', selectedTicket.id)
      }

      // Reload messages
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true })

      if (error) throw error

      setTicketMessages(data || [])
      setNewMessage('')
      setIsInternal(false)

      // Reload tickets
      await loadTickets()
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  async function handleAssignTicket(agentId: string) {
    if (!selectedTicket || !user) return

    try {
      setAssigning(true)

      const { error } = await supabase
        .from('support_tickets')
        .update({
          assigned_to: agentId,
          status: selectedTicket.status === 'new' ? 'open' : selectedTicket.status,
        })
        .eq('id', selectedTicket.id)

      if (error) throw error

      // Log audit event
      const agent = availableAgents.find(a => a.id === agentId)
      await supabase.rpc('log_audit_event', {
        p_admin_user_id: user.id,
        p_admin_email: user.email,
        p_admin_name: `${user.firstName} ${user.lastName}`,
        p_action_type: 'ticket_assigned',
        p_action_category: 'support',
        p_target_church_id: selectedTicket.church_id,
        p_target_church_name: selectedTicket.church_name || null,
        p_action_details: {
          ticket_id: selectedTicket.id,
          ticket_number: selectedTicket.ticket_number,
          assigned_to: agent ? `${agent.first_name} ${agent.last_name}` : 'Unknown',
        },
        p_severity: 'info'
      })

      await loadTickets()
      setShowTicketDialog(false)
      setSelectedTicket(null)
    } catch (error) {
      console.error('Error assigning ticket:', error)
      alert('Failed to assign ticket')
    } finally {
      setAssigning(false)
    }
  }

  async function handleChangeStatus(newStatus: string) {
    if (!selectedTicket || !user) return

    try {
      const updates: any = { status: newStatus }

      if (newStatus === 'resolved') {
        updates.resolved_at = new Date().toISOString()
      } else if (newStatus === 'closed') {
        updates.closed_at = new Date().toISOString()
      }

      const { error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', selectedTicket.id)

      if (error) throw error

      await loadTickets()
      setShowTicketDialog(false)
      setSelectedTicket(null)
    } catch (error) {
      console.error('Error changing status:', error)
      alert('Failed to change status')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading support tickets...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Ticket className="h-8 w-8" />
            Support Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage customer support requests
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateDialog(true)} variant="default">
            <Plus className="h-4 w-4 mr-2" />
            Create Ticket
          </Button>
          <Button onClick={() => loadTickets()} variant="outline">
            <MessageSquare className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{stats.open}</div>
            <p className="text-xs text-muted-foreground">Active tickets</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{stats.inProgress}</div>
            <p className="text-xs text-muted-foreground">Being worked on</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">Urgent attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              SLA Breach
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.slaBreach}</div>
            <p className="text-xs text-muted-foreground">Overdue response</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="open">
                  Open ({stats.open})
                </TabsTrigger>
                <TabsTrigger value="my">My Tickets</TabsTrigger>
                <TabsTrigger value="resolved">Resolved</TabsTrigger>
                <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="grid gap-4 md:grid-cols-5">
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_on_customer">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="bug_report">Bug Report</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>

              {(searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all') && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter('all')
                    setPriorityFilter('all')
                    setCategoryFilter('all')
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {filteredTickets.length !== tickets.length && (
              <p className="text-sm text-muted-foreground">
                Showing {filteredTickets.length} of {tickets.length} tickets
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets</CardTitle>
          <CardDescription>
            {filteredTickets.length} tickets matching your filters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket #</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Church</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No tickets found matching your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id} className={ticket.sla_breach ? 'bg-red-50 dark:bg-red-950' : ''}>
                      <TableCell className="font-mono font-medium">
                        {ticket.ticket_number}
                        {ticket.sla_breach && (
                          <Badge className="ml-2 bg-red-500 text-xs">SLA</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div>
                          <p className="font-medium truncate">{ticket.subject}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {ticket.submitter_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{ticket.church_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getCategoryLabel(ticket.category)}</Badge>
                      </TableCell>
                      <TableCell>{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell>
                        {ticket.assigned_to_name || <span className="text-muted-foreground">Unassigned</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewTicket(ticket)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Ticket Details Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              {selectedTicket?.ticket_number} - {selectedTicket?.subject}
            </DialogTitle>
            <DialogDescription>
              Created {selectedTicket && new Date(selectedTicket.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Church</p>
                  <p>{selectedTicket.church_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Submitter</p>
                  <p>{selectedTicket.submitter_name}</p>
                  {selectedTicket.submitter_email && (
                    <p className="text-xs text-muted-foreground">{selectedTicket.submitter_email}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Category</p>
                  <Badge variant="outline">{getCategoryLabel(selectedTicket.category)}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Priority</p>
                  {getPriorityBadge(selectedTicket.priority)}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Assigned To</p>
                  {selectedTicket.assigned_to_name ? (
                    <p>{selectedTicket.assigned_to_name}</p>
                  ) : (
                    <Select onValueChange={handleAssignTicket} disabled={assigning}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.first_name} {agent.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm font-medium mb-2">Description</p>
                <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                  {selectedTicket.description}
                </div>
              </div>

              {/* Messages */}
              <div>
                <p className="text-sm font-medium mb-2">Messages ({ticketMessages.length})</p>
                <div className="space-y-3 max-h-96 overflow-y-auto border rounded-lg p-4">
                  {ticketMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-lg ${
                        msg.is_internal
                          ? 'bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-500'
                          : msg.author_type === 'support'
                          ? 'bg-blue-50 dark:bg-blue-950'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span className="font-medium text-sm">{msg.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {msg.author_type}
                          </Badge>
                          {msg.is_internal && (
                            <Badge className="bg-yellow-500 text-xs">Internal Note</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reply Form */}
              <div className="space-y-3">
                <Label>Reply to Ticket</Label>
                <Textarea
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={4}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="internal"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="internal" className="text-sm">
                      Internal note (not visible to customer)
                    </label>
                  </div>
                  <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()}>
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-4 border-t">
                <Select onValueChange={handleChangeStatus} value={selectedTicket.status}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleCreateTicket}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Create New Support Ticket
              </DialogTitle>
              <DialogDescription>
                Create a support ticket on behalf of a church
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Church Selection */}
              <div className="space-y-2">
                <Label htmlFor="church">Church *</Label>
                <Select
                  value={newTicket.church_id}
                  onValueChange={(value) => {
                    const church = churches.find(c => c.id === value)
                    setNewTicket({
                      ...newTicket,
                      church_id: value,
                      church_name: church?.display_name || church?.name || ''
                    })
                  }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a church" />
                  </SelectTrigger>
                  <SelectContent>
                    {churches.map((church) => (
                      <SelectItem key={church.id} value={church.id}>
                        {church.display_name || church.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Submitter Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="submitter_name">Submitter Name *</Label>
                  <Input
                    id="submitter_name"
                    value={newTicket.submitter_name}
                    onChange={(e) => setNewTicket({ ...newTicket, submitter_name: e.target.value })}
                    placeholder="Pastor John"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="submitter_email">Email</Label>
                  <Input
                    id="submitter_email"
                    type="email"
                    value={newTicket.submitter_email}
                    onChange={(e) => setNewTicket({ ...newTicket, submitter_email: e.target.value })}
                    placeholder="john@church.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="submitter_phone">Phone</Label>
                <Input
                  id="submitter_phone"
                  value={newTicket.submitter_phone}
                  onChange={(e) => setNewTicket({ ...newTicket, submitter_phone: e.target.value })}
                  placeholder="+1234567890"
                />
              </div>

              {/* Category and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={newTicket.category}
                    onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical Support</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="feature_request">Feature Request</SelectItem>
                      <SelectItem value="bug_report">Bug Report</SelectItem>
                      <SelectItem value="general">General Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low (72h)</SelectItem>
                      <SelectItem value="medium">Medium (24h)</SelectItem>
                      <SelectItem value="high">High (4h)</SelectItem>
                      <SelectItem value="critical">Critical (1h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Detailed description of the issue, steps to reproduce, error messages, etc."
                  rows={6}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Ticket
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
